import { useState, useEffect } from "react";
import { SupportService } from "@/services/SupportService";

interface FilePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  filename: string;
  uploadId: string;
}

export const FilePreviewModal: React.FC<FilePreviewModalProps> = ({
  isOpen,
  onClose,
  filename,
  uploadId,
}) => {
  const [content, setContent] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);

  const lowerFilename = filename.toLowerCase();
  const isJson = lowerFilename.endsWith(".json");
  const isText = lowerFilename.endsWith(".txt") || lowerFilename.endsWith(".log") ||
                 lowerFilename.includes("log") || lowerFilename.includes("error");

  useEffect(() => {
    if (isOpen && (isJson || isText)) {
      loadContent();
    }
  }, [isOpen, uploadId, filename]);

  const loadContent = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const text = await SupportService.downloadFile(uploadId, filename);

      if (isJson) {
        // Try to parse and format JSON
        try {
          const parsed = JSON.parse(text);
          setContent(JSON.stringify(parsed, null, 2));
        } catch {
          // If parsing fails, show raw content
          setContent(text);
        }
      } else {
        setContent(text);
      }
    } catch (err) {
      console.error("Error loading file:", err);
      setError(err instanceof Error ? err.message : "Failed to load file");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleDownload = () => {
    const downloadUrl = SupportService.getDownloadUrl(uploadId, filename);
    window.open(downloadUrl, "_blank");
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-4xl max-h-[90vh] bg-white rounded-lg shadow-xl flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              {isJson ? (
                <div className="w-8 h-8 rounded bg-yellow-100 flex items-center justify-center">
                  <svg className="w-4 h-4 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
              ) : (
                <div className="w-8 h-8 rounded bg-gray-100 flex items-center justify-center">
                  <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
              )}
              <div>
                <h3 className="text-lg font-medium text-gray-900">{filename}</h3>
                <p className="text-xs text-gray-500">
                  {isJson ? "JSON file" : "Text file"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleCopy}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-1"
              >
                {copySuccess ? (
                  <>
                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Copied!
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    Copy
                  </>
                )}
              </button>
              <button
                onClick={handleDownload}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download
              </button>
              <button
                onClick={onClose}
                className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-auto p-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <svg className="animate-spin h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center py-12">
                <svg className="w-12 h-12 text-red-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <p className="text-sm text-gray-600">{error}</p>
              </div>
            ) : (
              <pre className={`text-sm font-mono whitespace-pre-wrap break-words ${
                isJson ? "text-gray-800" : "text-gray-700"
              }`}>
                {isJson ? (
                  <JsonHighlighter content={content} />
                ) : (
                  content
                )}
              </pre>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Simple JSON syntax highlighter using React elements (no dangerouslySetInnerHTML)
const JsonHighlighter: React.FC<{ content: string }> = ({ content }) => {
  // Tokenize and colorize JSON
  const tokenize = (text: string): JSX.Element[] => {
    const tokens: JSX.Element[] = [];
    // Match: strings, numbers, booleans, null, or other characters
    const regex = /("(?:[^"\\]|\\.)*")|(-?\d+\.?\d*(?:[eE][+-]?\d+)?)|(\btrue\b|\bfalse\b)|(\bnull\b)|([{}[\]:,\s]+)/g;

    let match;
    let lastIndex = 0;
    let tokenIndex = 0;

    while ((match = regex.exec(text)) !== null) {
      const [fullMatch, str, num, bool, nul, punct] = match;

      // Add any skipped characters
      if (match.index > lastIndex) {
        tokens.push(<span key={tokenIndex++}>{text.slice(lastIndex, match.index)}</span>);
      }

      if (str) {
        // Check if this string is a key (followed by :)
        const rest = text.slice(match.index + fullMatch.length);
        const isKey = /^\s*:/.test(rest);
        tokens.push(
          <span key={tokenIndex++} className={isKey ? "text-purple-600" : "text-green-600"}>
            {str}
          </span>
        );
      } else if (num) {
        tokens.push(<span key={tokenIndex++} className="text-orange-600">{num}</span>);
      } else if (bool) {
        tokens.push(<span key={tokenIndex++} className="text-blue-600">{bool}</span>);
      } else if (nul) {
        tokens.push(<span key={tokenIndex++} className="text-gray-500">{nul}</span>);
      } else if (punct) {
        tokens.push(<span key={tokenIndex++}>{punct}</span>);
      }

      lastIndex = match.index + fullMatch.length;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      tokens.push(<span key={tokenIndex++}>{text.slice(lastIndex)}</span>);
    }

    return tokens;
  };

  return <>{tokenize(content)}</>;
};
